import React, { useMemo, useState, Fragment } from 'react';
import { RowData, Inputs, PackageType, BuildPayType } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange
}: ProjectionTableProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    activeReps: false,
    investment: false,
    revenue: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const showTooltip = (content: string, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.right + 8,
      y: rect.top
    });
  };
  const hideTooltip = () => {
    setTooltip(null);
  };
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply half sales scenario
      if (halfSales) {
        newRow.totalSales = row.totalSales * 0.5;
        newRow.grossProfit = row.grossProfit * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        // Halve all active counts
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        // Recalculate sales based on reduced active reps
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        newRow.totalSales = row.totalSales * retentionMultiplier * salesMultiplier;
        newRow.grossProfit = row.grossProfit * retentionMultiplier * salesMultiplier;
      }
      // Recalculate net profit and cumulative profit
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition]);
  // Recalculate cumulative profit for adjusted rows
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  // Helper to get formula explanation for each metric with actual numbers
  const getFormula = (metricKey: string, row: RowData, rowIndex: number): string => {
    const monthNum = rowIndex; // 0 = Launch, 1 = Month 1, etc.
    switch (metricKey) {
      case 'hires':
        return `Formula: Ad Spend ÷ Cost per Hire\n\nActual: ${fmtMoney(row.adSpend)} ÷ CPH = ${row.hires.toFixed(0)} hires`;
      case 'launched':
        const launchRate = row.hires > 0 ? (row.launched / row.hires * 100).toFixed(0) : 0;
        return `Formula: Hires × Launch %\n\nActual: ${row.hires.toFixed(0)} × ${launchRate}% = ${row.launched.toFixed(1)} launched`;
      case 'active30d':
        if (monthNum === 0) return 'Build period - no active reps yet';
        if (monthNum === 1) return 'Month 1 - reps not yet at 30 days';
        const prevMonth = finalRows[monthNum - 1];
        return `Formula: Previous month's launched × Active @ 30d %\n\nActual: ${prevMonth.launched.toFixed(1)} launched × retention = ${row.active30d.toFixed(1)} active`;
      case 'active60d':
        if (monthNum <= 2) return monthNum === 0 ? 'Build period' : 'Not enough time elapsed (need 60 days)';
        const twoMonthsAgo = finalRows[monthNum - 2];
        return `Formula: 2 months ago launched × 60d retention\n\nActual: ${twoMonthsAgo.launched.toFixed(1)} launched × retention = ${row.active60d.toFixed(1)} active`;
      case 'active90d':
        if (monthNum < 3) return monthNum === 0 ? 'Build period' : 'Building cohort (need 90 days)...';
        return `Formula: Sum of recent cohorts in 90-day window\n\nActual: Cohorts from months ${monthNum - 2} to ${monthNum} = ${row.active90d.toFixed(1)} active`;
      case 'veterans':
        if (monthNum <= 3) return 'No veterans yet (need 90+ days)';
        return `Formula: Sum of all cohorts 4+ months old\n\nActual: All cohorts before month ${monthNum - 3} = ${row.veterans.toFixed(1)} veterans`;
      case 'adSpend':
        const scenarioNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario active but ad spend unchanged' : '';
        return `Formula: Monthly ad spend × campaigns\n\nActual: ${fmtMoney(row.adSpend)} total ad spend${scenarioNote}`;
      case 'buildFee':
        if (row.buildFee === 0) return 'No build fee this month';
        return `Formula: Build & Implementation Fee\n\nActual: ${fmtMoney(row.buildFee)} (${monthNum === 0 ? 'Build' : `Month ${monthNum} payment`})`;
      case 'platformFee':
        return `Formula: HIRE platform monthly fee\n\nActual: ${fmtMoney(row.platformFee)} (system access & support)`;
      case 'campaignFee':
        return `Formula: HIRE campaign management fee × campaigns\n\nActual: ${fmtMoney(row.campaignFee)} (campaign management)`;
      case 'placementBonus':
        const pkg = inputs.pkg;
        if (pkg === 'edge') {
          return 'HIRE Edge package does not include placement bonuses';
        }
        if (monthNum === 0) {
          return 'Build period - no bonuses billed yet';
        }
        // Billing for PREVIOUS month's launched reps
        const prevMonthForPlacement = finalRows[monthNum - 1];
        const bonusPerLaunched = 250;
        const rawPlacementBonus = prevMonthForPlacement.launched * bonusPerLaunched;
        const roundedPlacementBonus = Math.floor(rawPlacementBonus / 250) * 250;
        const placementAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Launched reps reduced by 50%' : '';
        // Build cohort breakdown
        let placementBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        placementBreakdown += `Billing for reps launched in ${prevMonthForPlacement.period}:\n`;
        placementBreakdown += `• ${prevMonthForPlacement.launched.toFixed(1)} reps launched\n`;
        placementBreakdown += `• Billed the following month (this month)\n`;
        placementBreakdown += `• $250 per launched rep`;
        return `Formula: Previous month's launched reps × $250\n(Billed month after launch)\n\nActual: ${prevMonthForPlacement.launched.toFixed(1)} launched × $250 = ${fmtMoney(rawPlacementBonus)}\nRounded down: ${fmtMoney(roundedPlacementBonus)}${placementBreakdown}${placementAttritionNote}`;
      case 'retentionBonus':
        const pkgType = inputs.pkg;
        if (pkgType === 'edge') {
          return 'HIRE Edge package does not include retention bonuses';
        }
        if (monthNum === 0) {
          return 'Build period - no bonuses billed yet';
        }
        // Billing for PREVIOUS month's active reps at each milestone
        const prevMonthForRetention = finalRows[monthNum - 1];
        let bonus30d = 0;
        let bonus60d = 0;
        let bonus90d = 0;
        let explanation = "Formula: Previous month's active reps × $250\n";
        explanation += '(Billed month after hitting milestone)\n';
        explanation += 'Rounded down to nearest $250\n\n';
        if (pkgType === 'advantage' || pkgType === 'summit') {
          const raw30d = prevMonthForRetention.active30d * 250;
          bonus30d = Math.floor(raw30d / 250) * 250;
          explanation += `30d: ${prevMonthForRetention.active30d.toFixed(1)} active × $250 = ${fmtMoney(bonus30d)}\n`;
          const raw60d = prevMonthForRetention.active60d * 250;
          bonus60d = Math.floor(raw60d / 250) * 250;
          explanation += `60d: ${prevMonthForRetention.active60d.toFixed(1)} active × $250 = ${fmtMoney(bonus60d)}\n`;
          const raw90d = prevMonthForRetention.active90d * 250;
          bonus90d = Math.floor(raw90d / 250) * 250;
          explanation += `90d: ${prevMonthForRetention.active90d.toFixed(1)} active × $250 = ${fmtMoney(bonus90d)}\n`;
        }
        const totalBonus = bonus30d + bonus60d + bonus90d;
        explanation += `Total: ${fmtMoney(totalBonus)}`;
        // Build cohort breakdown
        let retentionBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        retentionBreakdown += `Billing for milestones hit in ${prevMonthForRetention.period}:\n`;
        if (prevMonthForRetention.active30d > 0) {
          // 30d active are from cohort 1 month prior
          const cohortMonth = monthNum - 2;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 30d: ${prevMonthForRetention.active30d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active60d > 0) {
          // 60d active are from cohort 2 months prior
          const cohortMonth = monthNum - 3;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 60d: ${prevMonthForRetention.active60d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active90d > 0) {
          // 90d active are from cohort 3 months prior
          const cohortMonth = monthNum - 4;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 90d: ${prevMonthForRetention.active90d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        const retentionAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return explanation + retentionBreakdown + retentionAttritionNote;
      case 'bonuses':
        return `Formula: Launch + Day 30 + Day 60 + Day 90 bonuses\n\nActual: ${fmtMoney(row.bonuses)} total bonuses this month`;
      case 'totalCost':
        return `Formula: Platform + Campaign + Ad Spend + Bonuses\n\nActual: ${fmtMoney(row.hireCost)} + ${fmtMoney(row.adSpend)} + ${fmtMoney(row.bonuses)}\n= ${fmtMoney(row.totalCost)}`;
      case 'totalSales':
        const salesNote = halfSales ? '\n\n⚠️ 50% Sales Scenario: Sales reduced by 50%' : '';
        const attrNote = doubleAttrition ? '\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return `Formula: (Active 90d × rookie rate) + (Veterans × vet rate)\n\nActual: (${row.active90d.toFixed(1)} rookies) + (${row.veterans.toFixed(1)} vets)\n= ${row.totalSales.toFixed(1)} total sales${salesNote}${attrNote}`;
      case 'realizationRate':
        return `Formula: Pullthrough / Realization Rate\n\nActual: ${(row.realizationRate * 100).toFixed(0)}% of sales are realized`;
      case 'grossRevenue':
        const revenueNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied to sales' : '';
        return `Formula: Total Sales × Realization Rate × Avg Ticket\n\nActual: ${row.totalSales.toFixed(1)} × ${(row.realizationRate * 100).toFixed(0)}% × ${fmtMoney(inputs.ticket)}\n= ${fmtMoney(row.grossRevenue)}${revenueNote}`;
      case 'grossProfit':
        const profitNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied' : '';
        return `Formula: Gross Revenue × Margin %\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}%\n= ${fmtMoney(row.grossProfit)}${profitNote}`;
      case 'netProfit':
        return `Formula: Gross Profit - Total Investment\n\nActual: ${fmtMoney(row.grossProfit)} - ${fmtMoney(row.totalCost)}\n= ${fmtMoney(row.netProfit)}`;
      case 'cumProfit':
        return `Formula: Sum of all net profits from Build to ${row.period}\n\nActual: Running total = ${fmtMoney(row.cumProfit)}`;
      default:
        return 'Calculation details';
    }
  };
  // Define metrics to display (rows in the transposed table)
  const metricGroups = [{
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-purple-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'hires',
      label: 'Reps',
      format: (v: number) => v.toLocaleString()
    }, {
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'veterans',
      label: 'Veterans',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'totalReps',
      label: 'Total Reps',
      format: (v: number) => v.toFixed(1),
      bold: true,
      calculated: true
    }]
  }, {
    id: 'investment',
    label: 'Monthly Investment',
    color: 'bg-red-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'adSpend',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'buildFee',
      label: 'Build & Implementation',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'platformFee',
      label: 'Platform Fee',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'campaignFee',
      label: 'Campaign Fee',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'placementBonus',
      label: 'Placement Bonus',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'retentionBonus',
      label: 'Retention Bonus',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }]
  }, {
    id: 'revenue',
    label: 'Revenue & Profit',
    color: 'bg-green-50',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'totalSales',
      label: 'Total Sales',
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Realization Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, {
      key: 'grossRevenue',
      label: 'Gross Revenue',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: 'Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'netProfit',
      label: 'Net Profit',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }]
  }];
  return <div className="bg-white border-2 border-gray-200 rounded p-4 md:p-6 shadow-lg" data-id="element-191">
      <div className="flex items-center justify-between mb-4" data-id="element-192">
        <h2 className="text-lg md:text-xl font-bold text-navy" data-id="element-193">
          12-Month Projection
        </h2>

        <div className="flex items-center gap-3" data-id="element-194">
          {/* Split Toggle Button */}
          <button onClick={() => {
          // Toggle between upfront and split payment
          onInputsChange({
            ...inputs,
            buildPay: inputs.buildPay === 'upfront' ? 'two' : 'upfront'
          });
        }} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${inputs.buildPay === 'two' ? 'bg-amber text-navy border-amber' : 'bg-white text-gray-700 border-gray-300 hover:border-amber'}`} data-id="element-195">
            {inputs.buildPay === 'two' ? '✓ ' : ''}Split
          </button>

          {/* Package Dropdown */}
          <select value={inputs.pkg} onChange={e => onInputsChange({
          ...inputs,
          pkg: e.target.value as PackageType
        })} className="px-3 py-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-xs font-semibold" data-id="element-196">
            <option value="edge" data-id="element-197">HIRE Edge</option>
            <option value="advantage" data-id="element-198">HIRE Advantage</option>
            <option value="summit" data-id="element-199">HIRE Summit</option>
          </select>

          {/* Scenario Toggles */}
          <button onClick={() => setHalfSales(!halfSales)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-200">
            {halfSales ? '✓ ' : ''}50% Sales Scenario
          </button>
          <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-201">
            {doubleAttrition ? '✓ ' : ''}2x Attrition Scenario
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded border-2 border-gray-200" data-id="element-202">
        <table className="w-full border-collapse text-sm" data-id="element-203">
          <thead data-id="element-204">
            <tr className="bg-navy text-white" data-id="element-205">
              <th className="sticky left-0 z-20 bg-navy text-xs font-bold uppercase tracking-wide p-3 text-left min-w-[180px]" data-id="element-206">
                Metric
              </th>
              {finalRows.map(row => <th key={row.period} className="bg-navy text-xs font-bold uppercase tracking-wide p-3 text-center whitespace-nowrap min-w-[100px]" data-id="element-207">
                  {row.period}
                </th>)}
            </tr>
          </thead>
          <tbody className="bg-white" data-id="element-208">
            {metricGroups.map((group, groupIdx) => {
            const isExpanded = group.collapsible ? expandedSections[group.id as keyof typeof expandedSections] : true;
            const showDetails = !group.collapsible || isExpanded;
            return <Fragment key={group.id} data-id="element-209">
                  {/* Group Header Row */}
                  {!group.noHeader && <tr className={`${group.color} border-b-2 border-gray-300`} data-id="element-210">
                      <td className={`sticky left-0 z-10 ${group.color} p-3 font-bold text-gray-900 min-w-[180px] text-sm ${group.collapsible ? 'cursor-pointer hover:bg-opacity-80' : ''}`} onClick={() => group.collapsible && toggleSection(group.id as keyof typeof expandedSections)} data-id="element-211">
                        <div className="flex items-center gap-2" data-id="element-212">
                          {group.collapsible && <span className="text-navy" data-id="element-213">
                              {isExpanded ? '▼' : '▶'}
                            </span>}
                          <span className="uppercase tracking-wide text-sm" data-id="element-214">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {finalRows.map((row, rowIdx) => {
                  const isEvenColumn = rowIdx % 2 === 0;
                  const zebraClass = isEvenColumn ? '' : 'bg-gray-100/50';
                  return <td key={row.period} className={`${group.color} ${zebraClass} p-3 text-center font-bold text-gray-900 border-b-2 border-gray-300`} data-id="element-215">
                            {/* No data in headers */}
                          </td>;
                })}
                    </tr>}

                  {/* Detail Rows */}
                  {showDetails && group.metrics.map((metric, metricIdx) => {
                const isLastInGroup = metricIdx === group.metrics.length - 1;
                const borderClass = isLastInGroup ? 'border-b-2 border-gray-300' : 'border-b border-gray-200';
                const isEvenRow = metricIdx % 2 === 0;
                const rowBgClass = isEvenRow ? 'bg-white' : 'bg-gray-50/30';
                return <tr key={metric.key} className={`hover:bg-gray-100 h-14 ${group.noHeader ? group.color : rowBgClass}`} data-id="element-216">
                          <td className={`sticky left-0 z-10 ${group.noHeader ? group.color : rowBgClass} p-3 ${group.noHeader ? '' : 'pl-8'} text-gray-900 ${metric.bold ? 'font-bold text-sm' : 'font-medium text-sm'} ${borderClass} min-w-[180px]`} data-id="element-217">
                            {metric.label}
                          </td>
                          {finalRows.map((row, rowIdx) => {
                    let value: number;
                    if (metric.key === 'totalReps') {
                      value = row.launched + row.active30d + row.active60d + row.active90d + row.veterans;
                    } else if (metric.key === 'placementBonus') {
                      // Placement bonus: Billed the month AFTER launch
                      // So we look at PREVIOUS month's launched reps
                      const pkg = inputs.pkg;
                      if (pkg === 'advantage' || pkg === 'summit') {
                        if (rowIdx === 0) {
                          // Build period - no bonuses yet
                          value = 0;
                        } else {
                          // Bill for previous month's launched reps
                          const prevRow = finalRows[rowIdx - 1];
                          const rawBonus = prevRow.launched * 250;
                          value = Math.floor(rawBonus / 250) * 250;
                        }
                      } else {
                        value = 0;
                      }
                    } else if (metric.key === 'retentionBonus') {
                      // Retention bonus: Billed the month AFTER hitting milestone
                      // So we look at PREVIOUS month's active counts
                      const pkg = inputs.pkg;
                      let bonus = 0;
                      if (pkg === 'advantage' || pkg === 'summit') {
                        if (rowIdx === 0) {
                          // Build period - no bonuses yet
                          bonus = 0;
                        } else {
                          // Bill for previous month's active reps at each milestone
                          const prevRow = finalRows[rowIdx - 1];
                          const raw30d = prevRow.active30d * 250;
                          bonus += Math.floor(raw30d / 250) * 250;
                          const raw60d = prevRow.active60d * 250;
                          bonus += Math.floor(raw60d / 250) * 250;
                          const raw90d = prevRow.active90d * 250;
                          bonus += Math.floor(raw90d / 250) * 250;
                        }
                      }
                      value = bonus;
                    } else {
                      value = row[metric.key as keyof RowData] as number;
                    }
                    const formatted = metric.format(value);
                    const isEvenColumn = rowIdx % 2 === 0;
                    const columnShade = isEvenColumn ? '' : 'bg-gray-50/40';
                    let cellClass = `p-3 text-center whitespace-nowrap ${borderClass} relative group min-w-[100px] text-sm ${group.noHeader ? group.color : `${rowBgClass} ${columnShade}`}`;
                    if (metric.highlight) {
                      if (value >= 0) {
                        cellClass += ' text-green-600 font-bold';
                      } else {
                        cellClass += ' text-red-600 font-bold';
                      }
                    } else if (metric.bold) {
                      cellClass += ' font-bold text-gray-900';
                    } else {
                      cellClass += ' text-gray-700';
                    }
                    return <td key={row.period} className={cellClass} data-id="element-218">
                                <div className="flex items-center justify-center gap-1" data-id="element-219">
                                  <span data-id="element-220">{formatted}</span>
                                  {metric.key !== 'totalReps' && <button className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 rounded-full bg-navy text-white text-[10px] flex items-center justify-center hover:bg-amber hover:text-navy flex-shrink-0" onMouseEnter={e => showTooltip(getFormula(metric.key, row, rowIdx), e)} onMouseLeave={hideTooltip} onClick={e => {
                          e.stopPropagation();
                          if (tooltip) hideTooltip();else showTooltip(getFormula(metric.key, row, rowIdx), e);
                        }} data-id="element-221">
                                      ?
                                    </button>}
                                </div>
                              </td>;
                  })}
                        </tr>;
              })}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>

      {/* Tooltip */}
      {tooltip && <div className="fixed z-50 bg-navy text-white p-3 rounded shadow-xl border-2 border-amber max-w-sm text-xs whitespace-pre-line pointer-events-none" style={{
      left: tooltip.x,
      top: tooltip.y
    }} data-id="element-222">
          {tooltip.content}
        </div>}

      <div className="text-sm text-gray-600 mt-4 leading-relaxed" data-id="element-223">
        Bonuses: Advantage/Summit packages include $250 bonuses at Launch, 30
        days, 60 days, and 90 days for each active rep at those milestones.
      </div>
    </div>;
}